import React from "react";
import { Copy, Check, ExternalLink, Landmark, Smartphone, Mail, Phone, CheckCircle2, DollarSign } from "lucide-react";
import { useSiteContent } from "../admin/SiteContentContext";

const METHODS = [
  { id: "cashapp", label: "Cash App", color: "text-[#00d64f]" },
  { id: "zelle", label: "Zelle", color: "text-[#a0c6ff]" },
  { id: "venmo", label: "Venmo", color: "text-[#8ab6ff]" },
  { id: "bank", label: "Bank", color: "text-[#c7e34a]" },
];

function CopyField({ label, value }) {
  const [copied, setCopied] = React.useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1600); } catch {}
  };
  return (
    <div className="bg-[#0b1220] border border-white/10 rounded-xl p-3 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-[11px] text-slate-500 uppercase tracking-widest">{label}</div>
        <div className="text-white text-sm font-medium truncate">{value}</div>
      </div>
      <button type="button" onClick={copy} className="shrink-0 text-xs px-3 py-1.5 rounded-full border border-white/10 bg-white/5 text-slate-200 hover:border-[#c7e34a]/40 flex items-center gap-1.5">
        {copied ? <><Check className="w-3.5 h-3.5 text-[#c7e34a]" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
      </button>
    </div>
  );
}

/**
 * Props:
 *  - amount (number)
 *  - purpose (string): e.g. 'Consultation deposit' or 'Companion care — 2 hours'
 *  - meta (object): extra details to store with the payment record
 *  - onConfirmed(record): called when user marks the payment sent
 */
export default function PaymentOptions({ amount, purpose, meta = {}, onConfirmed }) {
  const { content } = useSiteContent();
  const p = content.paymentSettings;
  const [method, setMethod] = React.useState("cashapp");
  const [reference, setReference] = React.useState("");
  const [confirmed, setConfirmed] = React.useState(false);

  const amt = Number(amount || 0).toFixed(2);
  const note = encodeURIComponent(`${purpose || "3E DanCare"}${meta.name ? " — " + meta.name : ""}`);

  const cashappLink = `https://cash.app/${encodeURIComponent((p.cashapp || "").replace(/^\$/, "$"))}/${amt}`;
  const venmoLink = `${p.venmoUrl || "https://venmo.com/" + (p.venmo || "").replace(/^@/, "")}?txn=pay&amount=${amt}&note=${note}`;

  const qrFor = (data) => `https://api.qrserver.com/v1/create-qr-code/?size=220x220&bgcolor=ffffff&color=0b1220&margin=4&data=${encodeURIComponent(data)}`;

  const markSent = () => {
    const record = {
      amount: Number(amount || 0),
      currency: p.currency || "USD",
      method,
      purpose,
      reference: reference.trim(),
      meta,
      at: Date.now(),
      status: "reported",
    };
    try {
      const list = JSON.parse(localStorage.getItem("3e_payments") || "[]");
      list.push(record);
      localStorage.setItem("3e_payments", JSON.stringify(list));
    } catch {}
    setConfirmed(true);
    onConfirmed && onConfirmed(record);
  };

  if (confirmed) {
    return (
      <div className="text-center py-6">
        <div className="w-14 h-14 rounded-full bg-[#c7e34a]/15 flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-7 h-7 text-[#c7e34a]" />
        </div>
        <h4 className="font-serif-display text-white text-xl mt-4">Payment reported.</h4>
        <p className="text-slate-300 text-sm mt-2 max-w-sm mx-auto">
          Thank you. We'll verify ${amt} via {METHODS.find(m => m.id === method)?.label} and reach out to confirm.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] text-slate-500 uppercase tracking-widest">Amount due</div>
          <div className="flex items-baseline gap-1 text-white">
            <DollarSign className="w-5 h-5 text-[#c7e34a]" />
            <span className="font-serif-display text-4xl">{amt}</span>
            <span className="text-slate-400 text-sm ml-1">{p.currency || "USD"}</span>
          </div>
          {purpose && <div className="text-xs text-slate-400 mt-1">For: {purpose}</div>}
        </div>
      </div>

      <div className="inline-flex p-1 rounded-full bg-white/5 border border-white/10 flex-wrap">
        {METHODS.map(m => (
          <button key={m.id} type="button" onClick={() => setMethod(m.id)}
            className={`px-4 py-1.5 rounded-full text-sm transition ${method === m.id ? "bg-[#c7e34a] text-[#0b1220] font-semibold" : "text-slate-300 hover:text-white"}`}>
            {m.label}
          </button>
        ))}
      </div>

      <div className="card-soft p-5">
        {method === "cashapp" && (
          <div className="grid sm:grid-cols-[auto,1fr] gap-5 items-start">
            <div className="shrink-0 w-32 h-32 bg-white rounded-xl p-1.5 mx-auto sm:mx-0">
              <img src={qrFor(cashappLink)} alt="Cash App QR" className="w-full h-full object-contain rounded-lg" />
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-[#00d64f] text-xs uppercase tracking-widest font-semibold">
                <Smartphone className="w-3.5 h-3.5" /> Send via Cash App
              </div>
              <CopyField label="Cashtag" value={p.cashapp} />
              <a href={cashappLink} target="_blank" rel="noreferrer" className="btn-ghost text-sm w-full justify-center">
                Open Cash App <ExternalLink className="w-3.5 h-3.5" />
              </a>
              <p className="text-xs text-slate-400">Scan the QR with your phone or tap “Open Cash App”.</p>
            </div>
          </div>
        )}

        {method === "zelle" && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-[#a0c6ff] text-xs uppercase tracking-widest font-semibold">
              <Mail className="w-3.5 h-3.5" /> Send via Zelle
            </div>
            <CopyField label="Zelle email" value={p.zelleEmail} />
            <CopyField label="Zelle phone" value={p.zellePhone} />
            <p className="text-xs text-slate-400">Open your bank app, choose Zelle, and send to the email or phone above.</p>
          </div>
        )}

        {method === "venmo" && (
          <div className="grid sm:grid-cols-[auto,1fr] gap-5 items-start">
            <div className="shrink-0 w-32 h-32 bg-white rounded-xl p-1.5 mx-auto sm:mx-0">
              <img src={qrFor(venmoLink)} alt="Venmo QR" className="w-full h-full object-contain rounded-lg" />
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-[#8ab6ff] text-xs uppercase tracking-widest font-semibold">
                <Smartphone className="w-3.5 h-3.5" /> Send via Venmo
              </div>
              <CopyField label="Venmo handle" value={p.venmo} />
              <a href={venmoLink} target="_blank" rel="noreferrer" className="btn-ghost text-sm w-full justify-center">
                Open Venmo <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        )}

        {method === "bank" && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-[#c7e34a] text-xs uppercase tracking-widest font-semibold">
              <Landmark className="w-3.5 h-3.5" /> Bank transfer
            </div>
            <CopyField label="Account name" value={p.bankName} />
            <div className="grid sm:grid-cols-2 gap-3">
              <CopyField label="Account" value={p.bankAccount} />
              <CopyField label="Routing" value={p.bankRouting} />
            </div>
            <p className="text-xs text-slate-400">{p.bankInstructions}</p>
            <a href={`tel:${(p.zellePhone || "").replace(/[^0-9+]/g, "")}`} className="btn-ghost text-sm w-full justify-center">
              <Phone className="w-3.5 h-3.5" /> Call for details
            </a>
          </div>
        )}
      </div>

      <div className="card-soft p-5">
        <h5 className="text-white font-semibold mb-1">After you send</h5>
        <p className="text-xs text-slate-400 mb-3">{p.payNote}</p>
        <label className="text-xs text-slate-400 mb-1 block">Transaction/reference ID (optional)</label>
        <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. CashApp #ABC123"
          className="w-full bg-[#0b1220] border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-[#c7e34a]/60" />
        <button type="button" onClick={markSent} className="btn-lime w-full justify-center mt-4">
          <CheckCircle2 className="w-4 h-4" /> I’ve sent ${amt}
        </button>
      </div>
    </div>
  );
}
